<template>
  <public-layout>

    <!-- ═══════ BANNER ═══════ -->
    <div class="bs-banner">
      <div class="bs-banner__dots"></div>
      <div class="sc-container bs-banner__inner">
        <p class="bs-eyebrow">Blog Search</p>
        <h1 class="bs-banner__title">
          Results for <span class="bs-banner__query">"{{ search || $route.query.search }}"</span>
        </h1>
        <p class="bs-banner__sub">{{ pagination.total || details.length }} post{{ (pagination.total || details.length) !== 1 ? 's' : '' }} found</p>

        <!-- Inline search -->
        <form class="bs-search-bar" @submit.prevent="doSearch">
          <input
            v-model="searchInput"
            type="text"
            placeholder="Search blog posts…"
            class="bs-search-input"
          />
          <button type="submit" class="bs-search-btn">
            <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            Search
          </button>
        </form>
      </div>
    </div>

    <!-- ═══════ CONTENT ═══════ -->
    <section class="sc-container bs-main">

      <!-- Grid -->
      <div v-if="details.length" class="bs-grid">
        <article v-for="data in details" :key="data.id" class="bs-card">
          <router-link :to="'/blog/' + data.slug" class="bs-card__thumb-link">
            <img
              v-if="data.images && data.images.length"
              :src="data.images[0].path"
              class="bs-card__thumb"
              alt=""
            />
            <img v-else-if="data.image" :src="data.image" class="bs-card__thumb" alt="" />
            <div v-else class="bs-card__thumb bs-card__nophoto">
              <svg width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            </div>
          </router-link>

          <div class="bs-card__body">
            <span v-if="data.category" class="bs-card__cat">{{ data.category.name }}</span>
            <router-link :to="'/blog/' + data.slug" class="bs-card__title">
              {{ data.title.length > 72 ? data.title.substring(0, 72) + '…' : data.title }}
            </router-link>
            <p class="bs-card__snippet">{{ stripHtml(data.description).substring(0, 110) }}…</p>
            <div class="bs-card__foot">
              <span class="bs-card__date">{{ formatDate2(data.created_at) }}</span>
              <router-link :to="'/blog/' + data.slug" class="bs-read-link">
                Read
                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </router-link>
            </div>
          </div>
        </article>
      </div>

      <!-- Empty -->
      <div v-else-if="loaded" class="bs-empty">
        <div class="bs-empty__icon">
          <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        </div>
        <h3>No results found</h3>
        <p>Try a different search term or browse all posts.</p>
        <router-link to="/blog" class="btn-ghost">Browse all posts</router-link>
      </div>

      <!-- Loading skeleton -->
      <div v-else class="bs-grid">
        <div v-for="n in 6" :key="n" class="bs-card bs-card--skeleton">
          <div class="bs-skel bs-skel--thumb"></div>
          <div class="bs-card__body">
            <div class="bs-skel bs-skel--line" style="width:40%;height:12px"></div>
            <div class="bs-skel bs-skel--line" style="width:90%;height:18px;margin-top:.5rem"></div>
            <div class="bs-skel bs-skel--line" style="width:75%;height:18px;margin-top:.25rem"></div>
            <div class="bs-skel bs-skel--line" style="width:55%;height:13px;margin-top:.75rem"></div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.lastPage > 1" class="bs-pagination">
        <button class="bs-pg-btn" :disabled="!pagination.prevPageUrl" @click="fetchData(pagination.prevPageUrl)">
          <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          Previous
        </button>
        <span class="bs-pg-info">Page {{ pagination.currentPage }} of {{ pagination.lastPage }}</span>
        <button class="bs-pg-btn" :disabled="!pagination.nextPageUrl" @click="fetchData(pagination.nextPageUrl)">
          Next
          <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>

    </section>

  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import axios from 'axios';
import moment from 'moment';
import { useSEO } from '@/utils/seo';

export default {
  name: 'BlogSearch',
  components: { PublicLayout },

  created() {
    this.searchInput = this.$route.query.search || '';
    this.fetchData();
  },

  watch: {
    '$route.query.search'(newVal) {
      this.searchInput = newVal || '';
      this.loaded = false;
      this.details = [];
      this.fetchData();
    },
  },

  data() {
    return {
      loaded: false,
      searchInput: '',
      details: [],
      search: '',
      pagination: {
        lastPage: 0,
        currentPage: 1,
        total: 0,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
    };
  },

  methods: {
    doSearch() {
      if (!this.searchInput.trim()) return;
      this.$router.push({ path: '/blog-search', query: { search: this.searchInput.trim() } });
    },

    fetchData(url) {
      const q = this.$route.query.search;
      if (!q) return;
      const endpoint = url || process.env.VUE_APP_API_URL + 'blog/blog-search/' + encodeURIComponent(q);
      axios(endpoint)
        .then(response => {
          if (response.status !== 200) return;
          const data = response.data;
          this.details = data.data.data;
          this.search  = data.search;
          this.configPagination(data.data);
          useSEO({
            title: 'Search: "' + this.search + '"',
            description: 'Blog search results for "' + this.search + '" on Structurecity.',
            noindex: true,
          });
        })
        .catch(() => {})
        .finally(() => { this.loaded = true; });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },

    stripHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },

    formatDate2(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },
};
</script>

<style scoped>
/* ── Banner ─────────────────────────────────────────────── */
.bs-banner {
  position: relative;
  margin-top: 68px;
  background: linear-gradient(135deg, #0d1f36 0%, #1e3a5f 55%, #1a4a7a 100%);
  padding: 4rem 1.5rem 3.5rem;
  overflow: hidden;
}
.bs-banner__dots {
  position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 36px 36px;
}
.bs-banner::after {
  content: ''; position: absolute; bottom: -1px; left: 0; right: 0;
  height: 56px; background: var(--surface-2, #f8fafc);
  clip-path: ellipse(60% 100% at 50% 100%);
}
.bs-banner__inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; align-items: center;
  text-align: center; gap: .75rem;
}
.bs-eyebrow {
  font-size: .75rem; font-weight: 700; letter-spacing: .1em;
  text-transform: uppercase; color: var(--accent, #f59e0b); margin: 0;
}
.bs-banner__title {
  font-size: clamp(1.5rem, 3vw, 2.25rem); font-weight: 800;
  color: #fff; letter-spacing: -.02em; margin: 0; line-height: 1.2;
}
.bs-banner__query { color: var(--accent, #f59e0b); }
.bs-banner__sub { font-size: .9375rem; color: rgba(255,255,255,.6); margin: 0; }

/* Inline search bar */
.bs-search-bar {
  display: flex; gap: .5rem; width: 100%; max-width: 520px; margin-top: .5rem;
}
.bs-search-input {
  flex: 1; padding: .75rem 1.125rem;
  border: 1.5px solid rgba(255,255,255,.2);
  border-radius: var(--radius-full, 9999px);
  background: rgba(255,255,255,.1);
  color: #fff; font-size: .9375rem; font-family: inherit;
  outline: none; transition: border-color .15s, background .15s;
  backdrop-filter: blur(8px);
}
.bs-search-input::placeholder { color: rgba(255,255,255,.45); }
.bs-search-input:focus { border-color: rgba(255,255,255,.5); background: rgba(255,255,255,.15); }
.bs-search-btn {
  display: inline-flex; align-items: center; gap: .4rem;
  background: var(--accent, #f59e0b); color: #fff; border: none;
  border-radius: var(--radius-full, 9999px);
  padding: .75rem 1.375rem; font-size: .9375rem; font-weight: 700;
  cursor: pointer; white-space: nowrap;
  transition: background .15s;
}
.bs-search-btn:hover { background: #d97706; }

/* ── Main ───────────────────────────────────────────────── */
.bs-main { padding: 2.5rem 0 4rem; }

/* ── Grid ───────────────────────────────────────────────── */
.bs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}
.bs-card {
  background: #fff;
  border-radius: var(--radius-lg, 1.25rem);
  border: 1px solid var(--border-light, #f1f5f9);
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  overflow: hidden; display: flex; flex-direction: column;
  transition: box-shadow .2s, transform .2s;
}
.bs-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,.09); transform: translateY(-2px); }

.bs-card__thumb-link { display: block; flex-shrink: 0; }
.bs-card__thumb { width: 100%; height: 200px; object-fit: cover; display: block; }
.bs-card__nophoto {
  height: 200px; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f0f4f8, #e2eaf4); color: #94a3b8;
}

.bs-card__body { padding: 1.25rem; display: flex; flex-direction: column; flex: 1; }
.bs-card__cat {
  font-size: .7rem; font-weight: 700; letter-spacing: .05em;
  text-transform: uppercase; color: var(--primary, #1e3a5f); margin-bottom: .375rem;
}
.bs-card__title {
  font-size: .9375rem; font-weight: 700; color: var(--text, #111827);
  text-decoration: none; line-height: 1.45; display: block; margin-bottom: .5rem;
}
.bs-card__title:hover { color: var(--primary, #1e3a5f); }
.bs-card__snippet {
  font-size: .8125rem; color: var(--text-muted, #6b7280);
  line-height: 1.55; margin: 0 0 1rem; flex: 1;
}
.bs-card__foot {
  display: flex; align-items: center; justify-content: space-between;
  border-top: 1px solid var(--border-light, #f1f5f9); padding-top: .75rem; margin-top: auto;
}
.bs-card__date { font-size: .75rem; color: var(--text-muted, #6b7280); }
.bs-read-link {
  display: inline-flex; align-items: center; gap: .3rem;
  font-size: .8125rem; font-weight: 700; color: var(--primary, #1e3a5f);
  text-decoration: none; transition: gap .15s;
}
.bs-read-link:hover { gap: .5rem; }

/* ── Skeleton ───────────────────────────────────────────── */
.bs-skel {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: .375rem;
}
.bs-skel--thumb { height: 200px; border-radius: 0; }
@keyframes shimmer { to { background-position: -200% 0; } }

/* ── Empty ──────────────────────────────────────────────── */
.bs-empty {
  display: flex; flex-direction: column; align-items: center;
  gap: 1rem; padding: 5rem 1rem; text-align: center;
  color: var(--text-muted, #6b7280);
}
.bs-empty__icon {
  width: 88px; height: 88px; border-radius: 50%;
  background: var(--surface-3, #f1f5f9);
  display: flex; align-items: center; justify-content: center; color: #cbd5e1;
}
.bs-empty h3 { font-size: 1.125rem; font-weight: 700; color: var(--text, #111827); margin: 0; }
.bs-empty p  { font-size: .9375rem; margin: 0; }

/* ── Pagination ─────────────────────────────────────────── */
.bs-pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 1rem; padding: 2.5rem 0 0;
}
.bs-pg-btn {
  display: inline-flex; align-items: center; gap: .375rem;
  background: #fff; border: 1.5px solid var(--border, #e5e7eb);
  border-radius: var(--radius, .625rem); padding: .5rem 1.125rem;
  font-size: .875rem; font-weight: 600; color: var(--text, #111827);
  cursor: pointer; transition: background .15s, border-color .15s;
}
.bs-pg-btn:disabled { opacity: .4; cursor: not-allowed; }
.bs-pg-btn:not(:disabled):hover { background: var(--surface-2, #f8fafc); border-color: var(--primary, #1e3a5f); }
.bs-pg-info { font-size: .875rem; color: var(--text-muted, #6b7280); }

/* ── Button ─────────────────────────────────────────────── */
.btn-ghost {
  display: inline-flex; align-items: center;
  background: none; color: var(--text-muted, #6b7280);
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: var(--radius-full, 9999px);
  padding: .625rem 1.375rem; font-size: .9375rem; font-weight: 600;
  cursor: pointer; text-decoration: none; transition: background .15s, color .15s;
}
.btn-ghost:hover { background: var(--surface-2, #f8fafc); color: var(--text, #111827); }

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 768px) {
  .bs-banner { padding: 3rem 1.25rem 2.5rem; }
  .bs-search-bar { flex-direction: column; }
  .bs-search-btn { width: 100%; justify-content: center; }
  .bs-grid { grid-template-columns: 1fr; }
}
</style>
